import React from 'react';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';

const DataProtectionPage: React.FC = () => {
  useSEO(pageConfigs.dataProtection);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.dataProtection} />
      <Header />

      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-6">
              Data Protection Policy
            </h1>
            <p className="text-lg text-slate-600">
              Last updated: November 2024
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none text-slate-600 space-y-8">
            
            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                1. Overview
              </h2>
              <p>
                3 Arrows Media is committed to protecting your personal data and respecting your privacy. This Data Protection Policy outlines how we collect, process, store, and protect your information in compliance with applicable data protection regulations including GDPR, CCPA, and other relevant laws.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                2. Legal Basis for Processing
              </h2>
              <p>We process your personal data based on:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Consent:</strong> You have provided explicit consent for data processing</li>
                <li><strong>Contract:</strong> Processing is necessary to fulfill a contract with you</li>
                <li><strong>Legal Obligation:</strong> We are required by law to process your data</li>
                <li><strong>Legitimate Interests:</strong> Processing serves our legitimate business interests</li>
                <li><strong>Vital Interests:</strong> Processing is necessary to protect vital interests</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                3. Data Retention
              </h2>
              <p>
                We retain your personal data only as long as necessary for the purposes outlined in this policy or as required by law. Retention periods vary depending on the type of data and the purpose of processing:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Contact information: Retained for the duration of our business relationship plus 3 years</li>
                <li>Marketing preferences: Retained until you unsubscribe</li>
                <li>Website analytics: Typically retained for up to 26 months</li>
                <li>Payment information: Retained only as long as necessary to complete transactions</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                4. Data Security Measures
              </h2>
              <p>We implement comprehensive security measures to protect your data:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure authentication mechanisms and access controls</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection practices</li>
                <li>Incident response procedures</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                5. Data Subject Rights
              </h2>
              <p>Under applicable data protection laws, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Right of Access:</strong> Access your personal data held by us</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate personal data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we process your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Object:</strong> Object to processing for direct marketing</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                6. International Data Transfers
              </h2>
              <p>
                If we transfer your data internationally, we ensure adequate safeguards are in place, including standard contractual clauses, binding corporate rules, or your explicit consent.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                7. Data Processing Agreements
              </h2>
              <p>
                Where we work with third-party processors, we ensure they are bound by data processing agreements that guarantee the same level of data protection and privacy as provided by this policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                8. Data Breach Notification
              </h2>
              <p>
                In the event of a data breach, we will notify affected individuals and relevant authorities as required by applicable law, typically within 72 hours of becoming aware of the breach.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-display font-bold text-slate-900 mb-4">
                9. Contact & Complaints
              </h2>
              <p>
                If you have questions about our data protection practices or wish to exercise your rights, please contact our Data Protection Officer:
              </p>
              <div className="mt-4 bg-slate-50 p-6 rounded-lg">
                <p><strong>3 Arrows Media - Data Protection Officer</strong></p>
                <p>Email: privacy@3arrowsmedia.com</p>
              </div>
              <p className="mt-4">
                You also have the right to lodge a complaint with your local data protection authority if you believe we have violated your data protection rights.
              </p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DataProtectionPage;
